import { useEffect, useState } from "react";
import { Wifi, CheckCircle2, XCircle, AlertTriangle, ArrowUp, ArrowDown, X } from "lucide-react";
import portalApi from "@/lib/portalApi";
import { useT } from "@/i18n";

export default function PortalServices() {
  const t = useT();
  const p = t.portal;
  const [services, setServices] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [requestModal, setRequestModal] = useState(null);
  const [details, setDetails] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    Promise.all([
      portalApi.get("/portal/services").then((r) => setServices(r.data)),
      portalApi.get("/portal/requests").then((r) => setRequests(r.data)),
    ]).finally(() => setLoading(false));
  }, []);

  const today = new Date().toISOString().slice(0, 10);

  const submitRequest = async () => {
    if (!requestModal) return;
    setSending(true);
    try {
      await portalApi.post("/portal/requests", { subscription_id: requestModal.subId, type: requestModal.type, details });
      const { data } = await portalApi.get("/portal/requests");
      setRequests(data); setRequestModal(null); setDetails("");
    } catch { /* */ }
    setSending(false);
  };

  const reqLabels = { upgrade: p.upgrade, downgrade: p.downgrade, cancel: p.cancel };
  const reqStatusBadge = { pending: "crm-badge crm-badge-orange", approved: "crm-badge crm-badge-green", rejected: "crm-badge crm-badge-red" };
  const reqStatusLabels = { pending: p.reqPending, approved: p.reqApproved, rejected: p.reqRejected };

  if (loading) return <div className="space-y-4">{[...Array(3)].map((_, i) => <div key={i} className="h-32 crm-card animate-pulse" />)}</div>;

  return (
    <div data-testid="portal-services">
      <h1 className="crm-heading mb-8">{p.myServicesTitle}</h1>

      {services.length === 0 ? (
        <div className="crm-card p-12 text-center" style={{ color: "var(--crm-text-dim)" }}>{p.noServiceFound}</div>
      ) : (
        <div className="space-y-4 mb-10">
          {services.map((s) => {
            const isOverdue = s.status === "active" && s.next_billing_date && s.next_billing_date < today;
            return (
              <div key={s.id} className="crm-card p-5">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: "var(--crm-accent-subtle)", color: "var(--crm-accent)" }}>
                      <Wifi className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-base font-bold" style={{ color: "var(--crm-text)" }}>{s.product_name}</p>
                      <p className="text-xs" style={{ color: "var(--crm-text-muted)" }}>{p.since} {s.start_date}</p>
                    </div>
                  </div>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold"
                    style={{ background: isOverdue ? "rgba(239,68,68,0.15)" : s.status === "active" ? "rgba(16,185,129,0.12)" : "rgba(148,163,184,0.12)",
                      color: isOverdue ? "#ef4444" : s.status === "active" ? "#10b981" : "#94a3b8" }}>
                    {isOverdue ? <><AlertTriangle className="w-3.5 h-3.5" /> {p.statusOverdue}</> : s.status === "active" ? <><CheckCircle2 className="w-3.5 h-3.5" /> {p.statusActive}</> : <><XCircle className="w-3.5 h-3.5" /> {p.statusCancelled}</>}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xl font-bold" style={{ color: "var(--crm-text)" }}>¥{s.monthly_amount.toLocaleString()}<span className="text-sm font-normal" style={{ color: "var(--crm-text-dim)" }}>{p.month}</span></p>
                    {s.next_billing_date && <p className="text-xs mt-1" style={{ color: "var(--crm-text-muted)" }}>{p.nextBilling} {s.next_billing_date}</p>}
                  </div>
                  {s.status === "active" && (
                    <div className="flex gap-2">
                      <button onClick={() => setRequestModal({ subId: s.id, type: "upgrade" })} className="crm-btn-secondary text-xs py-2 px-3"><ArrowUp className="w-3 h-3" /> {p.upgrade}</button>
                      <button onClick={() => setRequestModal({ subId: s.id, type: "downgrade" })} className="crm-btn-secondary text-xs py-2 px-3"><ArrowDown className="w-3 h-3" /> {p.downgrade}</button>
                      <button onClick={() => setRequestModal({ subId: s.id, type: "cancel" })}
                        className="text-xs py-2 px-3 rounded-lg font-medium inline-flex items-center gap-1.5"
                        style={{ color: "#ef4444", background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.15)" }}>
                        <X className="w-3 h-3" /> {p.cancel}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {requestModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center px-4" onClick={() => setRequestModal(null)}>
          <div className="crm-card p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h3 className="crm-subheading">{p.requestTitle} {reqLabels[requestModal.type]}</h3>
            <textarea value={details} onChange={(e) => setDetails(e.target.value)} rows={3} className="crm-input resize-none mb-4" placeholder={p.requestDesc} />
            <div className="flex gap-3">
              <button onClick={submitRequest} disabled={sending} className="crm-btn-primary text-sm">{sending ? "..." : p.sendRequest}</button>
              <button onClick={() => setRequestModal(null)} className="crm-btn-secondary text-sm">{p.cancelBtn}</button>
            </div>
          </div>
        </div>
      )}

      {requests.length > 0 && (
        <div>
          <h2 className="text-base font-bold mb-4" style={{ color: "var(--crm-text)" }}>{p.myRequests}</h2>
          <div className="crm-card overflow-hidden">
            <table className="crm-table">
              <thead><tr><th>{p.reqType}</th><th>{p.reqDetails}</th><th>{p.reqDate}</th><th>{p.reqStatus}</th></tr></thead>
              <tbody>
                {requests.map((r) => (
                  <tr key={r.id}>
                    <td className="primary">{reqLabels[r.type] || r.type}</td>
                    <td>{r.details || "—"}</td>
                    <td>{new Date(r.created_at).toLocaleDateString("pt-BR")}</td>
                    <td><span className={reqStatusBadge[r.status]}>{reqStatusLabels[r.status]}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
